<template>
  <div>
    <!-- Bot Chat Component -->
    <div class="bot-chat-container" :class="{ 'bot-visible': showBot, 'bot-hidden': hidingBot }">
      <img class="bot-image" src="@/assets/botrmbg.png" alt="Bot" @click="hideMessage" />
      <div class="bot-message" :class="{
        'message-visible': showMessage,
        'message-hidden': hidingMessage,
      }">
        <div v-if="isTyping" class="typing-animation">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <div v-else class="typed-message" v-html="typedContent"></div>
      </div>
    </div>
    <!-- Display the market summary quote -->
    <div class="market-summary">
      <h3>Market Summary</h3>
      <div v-if="marketSummary" v-html="renderedSummary"></div>
      <p v-else>Loading summary…</p>
    </div>
    <CryptoPopup v-if="showPopup" :crypto="selectedCrypto" @close="closePopup" />

    <!-- Top Series Section -->
    <section class="top-series">
      <TopSeries title="Hot Coins" :cryptos="hotCoins" :openPopup="openPopup" />
      <TopSeries title="Top Losers" :cryptos="topLosers" :openPopup="openPopup" />
      <TopSeries title="Top Gainers" :cryptos="topGainers" :openPopup="openPopup" />
      <TopSeries title="Top Volume" :cryptos="topVolume" :openPopup="openPopup" />
    </section>

    <!-- Search Bar -->
    <!-- <div class="search-bar">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Search by symbol or name..." 
        @keyup.enter="filterCryptos"
      />
      <button @click="filterCryptos">Search</button>
    </div> -->

    <div class="search-bar">
      <input type="text" v-model="searchQuery" placeholder="Search for a coin..." @input="filterCryptos" />
    </div>

    <!-- Existing crypto list -->
    <div class="crypto-table-wrapper">
      <div class="crypto-table">
        <div v-for="(row, rowIndex) in filteredRows" :key="rowIndex">
          <div class="crypto-row">
            <div class="crypto-item animate__animated animate__fadeInUp" v-for="crypto in row" :key="crypto.symbol"
              @click="openPopup(crypto)" @mouseover="showTooltip(crypto.uuid)" @mouseleave="hideTooltip">
              <div :class="[
                'crypto-bar',
                {
                  'positive-bar': crypto.change > 0,
                  'negative-bar': crypto.change < 0,
                },
              ]"></div>
              <div class="crypto-info">
                <div class="crypto-symbol">
                  <h3>{{ crypto.symbol }}</h3>
                  <p>{{ crypto.name }}</p>
                </div>
                <div class="crypto-price">
                  <p class="price">${{ crypto.price.toFixed(6) }}</p>
                </div>
                <div class="crypto-change">
                  <p :class="{
                    positive: crypto.change > 0,
                    negative: crypto.change < 0,
                  }">
                    {{ crypto.change > 0 ? "+" : ""
                    }}{{ crypto.change.toFixed(2) }}%
                  </p>
                  <p :class="{
                    positive: crypto.changeAmount > 0,
                    negative: crypto.changeAmount < 0,
                  }">
                    {{ crypto.changeAmount.toFixed(6) }}
                  </p>
                </div>
              </div>
              <!-- Tooltip element -->
              <div :class="[
                'tooltip',
                {
                  'tooltip-visible':
                    tooltipVisible && activeCrypto === crypto.uuid,
                },
              ]">
                Full chart
              </div>
            </div>
          </div>
          <div v-if="rowIndex < filteredRows.length - 1" class="row-divider"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import TopSeries from "../MarketPage/TopSeries.vue";
import CryptoPopup from "../MarketPage/CryptoPopup.vue";
import { GoogleGenerativeAI } from "@google/generative-ai";
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt({
  html: true,        // allow inline HTML
  linkify: true,        // autolink URLs
  typographer: true,
})

const DEPLOY_URL = process.env.VUE_APP_DEPLOY_URL;
const BINANCE_API_KEY = process.env.VUE_APP_BINANCE_API_KEY;
const apiKey = process.env.VUE_APP_GEMINI_API_KEY;
const genAI = new GoogleGenerativeAI(apiKey);

export default {
  name: "CryptoWatch",
  props: {
    marketSummary: {
      type: String,
      default: ''
    }
  },
  computed: {
    renderedSummary() {
      return md.render(this.marketSummary)
    }
  },
  components: {
    TopSeries,
    CryptoPopup,
  },
  data() {
    return {
      cryptos: [],
      hotCoins: [],
      topGainers: [],
      topLosers: [],
      topVolume: [],
      rows: [[], [], []],
      filteredRows: [[], [], []],
      showPopup: false,
      selectedCrypto: null,
      searchQuery: "", // For storing the search input
      tooltipVisible: false, // Tooltip visibility flag
      activeCrypto: null, // Store the UUID of the active crypto for tooltip
      // marketSummary: null, // To store the generated market summary
      // Bot Chat data
      showBot: false,
      hidingBot: false,
      showMessage: false,
      hidingMessage: false,
      isTyping: false,
      botMessage: "",
      typedContent: "",
      typingSpeed: 50, // milliseconds between characters
      typingIndex: 0,
      typingTimer: null,
      botHideTimer: null,
      words: [],
      currentWordIndex: 0,
    };
  },
  created() {
    this.fetchCryptos();
  },
  methods: {
    // Bot Chat method
    async startBotAnimation() {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      if (this.botHideTimer) {
        clearTimeout(this.botHideTimer);
      }

      this.hidingBot = false;
      this.hidingMessage = false;
      this.typedContent = "";

      this.showBot = true;

      setTimeout(async () => {
        this.showMessage = true;
        this.isTyping = true;

        // Fetch insights from Gemini API
        const insights = await this.generateCryptoInsights();
        this.botMessage = insights; // Update the bot message with the generated insights

        setTimeout(() => {
          this.isTyping = false;
          this.startRealisticTyping();
        }, 500);
      }, 400);
    },

    startRealisticTyping() {
      // Use character-by-character typing for more realistic effect
      this.typedContent = "";
      let charIndex = 0;

      // Simplified version to address potential issues
      const typeNextChar = () => {
        if (charIndex < this.botMessage.length) {
          // Get the current character for typing
          const currentChar = this.botMessage.charAt(charIndex);

          // Add character to typed content with HTML formatting
          if (currentChar === "\n") {
            this.typedContent += "<br>";
          } else {
            this.typedContent += currentChar;
          }

          charIndex++;

          // Calculate a simpler typing speed based on character type
          let delay;

          // Basic delays based on character type
          if ([".", "!", "?"].includes(currentChar)) {
            delay = Math.random() * 100 + 80; // End of sentence
          } else if ([",", ":", ";"].includes(currentChar)) {
            delay = Math.random() * 60 + 40; // Mid-sentence pause
          } else if (currentChar === " ") {
            delay = Math.random() * 20 + 10; // Word break
          } else if (currentChar === "\n") {
            delay = Math.random() * 120 + 80; // Line break
          } else if (!isNaN(parseInt(currentChar))) {
            delay = Math.random() * 25 + 15; // Numbers
          } else {
            // Regular characters with occasional speed variation
            delay =
              Math.random() < 0.3
                ? Math.random() * 8 + 5 // Fast typing (30% chance)
                : Math.random() * 15 + 10; // Normal typing (70% chance)
          }

          // Add occasional small thinking pause (2% chance)
          if (Math.random() < 0.02) {
            delay += Math.random() * 150 + 50;
          }

          // Schedule typing the next character
          this.typingTimer = setTimeout(typeNextChar, delay);
        } else {
          // Typing complete, schedule the hide
          this.scheduleHideBot();
        }
      };

      // Start typing with a small initial delay
      setTimeout(typeNextChar, 300);
    },

    scheduleHideBot() {
      this.botHideTimer = setTimeout(() => {
        this.hideBot();
      }, 30000);
    },

    hideMessage() {
      this.hidingMessage = true;
    },

    hideBot() {
      this.hidingMessage = true;

      setTimeout(() => {
        this.hidingBot = true;

        setTimeout(() => {
          this.showBot = false;
          this.showMessage = false;
          this.hidingBot = false;
          this.hidingMessage = false;
          this.typedContent = "";
        }, 500);
      }, 300);
    },
    async generateCryptoInsights() {
      try {
        const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

        const chat = await model.startChat({
          history: [
            {
              role: "user",
              parts: [
                {
                  text: "You're a crypto trading expert. Provide a general analysis of the cryptocurrency market.",
                },
              ],
            },
            {
              role: "model",
              parts: [
                {
                  text: "Analyzing the current state of the cryptocurrency market.",
                },
              ],
            },
          ],
          generationConfig: {
            maxOutputTokens: 500,
          },
        });

        const prompt = `Provide a general cryptocurrency market analysis including:
        - Overall market trends
        - Bitcoin and Ethereum price movements
        - Key altcoin trends`;

        const result = await chat.sendMessage(prompt);
        const response = result.response;
        return response.text();
      } catch (error) {
        console.error("Error generating general crypto insights:", error);
        return "Unable to analyze the cryptocurrency market at the moment. Please try again later.";
      }
    },

    async fetchCryptos() {
      try {
        // Make a request to your backend, which will call the Binance API
        const response = await axios.post(`${DEPLOY_URL}/proxy`, {
          url: "https://api.binance.us/api/v3/ticker/24hr",
          method: "GET",
          headers: {
            "X-MBX-APIKEY": BINANCE_API_KEY,
            Accept: "application/json",
          },
        });

        // Filter the response to only include USDT pairs (which are priced in USD)
        const usdPairs = response.data.filter((ticker) =>
          ticker.symbol.endsWith("USDT")
        );

        // Process the response data from Binance API
        this.cryptos = usdPairs.map((ticker) => ({
          symbol: ticker.symbol.replace("USDT", ""), // Remove 'USDT' to show just the crypto symbol
          name: ticker.symbol.replace("USDT", ""), // Using symbol as name for simplicity
          price: parseFloat(ticker.lastPrice),
          change: parseFloat(ticker.priceChangePercent),
          changeAmount: parseFloat(ticker.priceChange),
          volume: parseFloat(ticker.volume),
          uuid: ticker.symbol, // Using symbol as uuid substitute
        }));

        // Categorize the data
        this.hotCoins = this.getTopCoins(this.cryptos, "changeAmount", 3);
        this.topGainers = this.getTopCoins(this.cryptos, "change", 3);
        this.topLosers = this.getTopCoins(this.cryptos, "change", 3, true);
        this.topVolume = this.getTopCoins(this.cryptos, "symbol", 3);

        if (this.cryptos.length > 0) {
          this.selectedCrypto = this.cryptos[0].symbol; // Set default selected crypto
        }
        this.distributeCryptos();
        // await this.generateMarketSummary(); // Generate market summary after fetching cryptos
      } catch (error) {
        console.error("Error fetching cryptos:", error);
      }
    },
    getTopCoins(coins, sortBy, limit, ascending = false) {
      return coins
        .slice()
        .sort((a, b) =>
          ascending ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy]
        )
        .slice(0, limit);
    },
    distributeCryptos() {
      // Distribute cryptos into rows for display
      this.rows = [[], [], []]; // Reset rows
      for (let i = 0; i < this.cryptos.length; i++) {
        this.rows[i % 3].push(this.cryptos[i]);
      }
      this.filteredRows = this.rows; // Initialize filteredRows with all cryptos
    },
    filterCryptos() {
      const query = this.searchQuery.toLowerCase();

      if (!query) {
        this.filteredRows = this.rows;
        return;
      }

      const filteredCryptos = this.cryptos.filter(
        (crypto) =>
          crypto.symbol.toLowerCase().includes(query) ||
          crypto.name.toLowerCase().includes(query)
      );

      // Distribute filtered cryptos into rows
      this.filteredRows = [[], [], []];
      for (let i = 0; i < filteredCryptos.length; i++) {
        this.filteredRows[i % 3].push(filteredCryptos[i]);
      }
    },
    // async generateMarketSummary() {
    //   try {
    //     const allCryptos = this.cryptos; // Use all cryptos
    //     const promptData = allCryptos
    //       .map((crypto) => {
    //         return `${crypto.symbol} (${crypto.name}) - $${crypto.price.toFixed(
    //           2
    //         )} - ${crypto.changeAmount.toFixed(2)}%`;
    //       })
    //       .join("\n");

    //     const openAiPrompt = `Here is the data for all cryptocurrencies:\n${promptData}\n\nBased on the above data, provide a summary of the overall market trend. Consider factors like the general direction (increase/decrease), and any significant fluctuations or patterns. Please give a concise summary that starts with "The market trend today tends to..."`;

    //     const summary = await gptServices([
    //       { role: 'user', content: openAiPrompt }
    //     ]);

    //     this.marketSummary = summary;
    //   } catch (error) {
    //     console.error("Error generating market summary:", error);
    //     this.marketSummary = "Unable to generate market summary at this time.";
    //   }
    // },
    openPopup(crypto) {
      this.selectedCrypto = crypto;
      this.showPopup = true;
    },
    closePopup() {
      this.showPopup = false;
      this.selectedCrypto = null;
    },
    showTooltip(uuid) {
      this.activeCrypto = uuid;
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
  },

  mounted() {
    this.startBotAnimation();
  },
};
</script>

<style scoped>
@import "animate.css";

body {
  font-family: "Segoe UI", Arial, sans-serif;
}

.top-series {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 20px 0;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

/* For mobile screens (typically 768px and below) */
@media (max-width: 768px) {
  .top-series {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
}

.crypto-watch {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
}

.header {
  color: #000000;
  opacity: 1;
  text-align: left;
  margin-bottom: 20px;
  animation: fadeInDown 0.5s;
}

.crypto-section {
  background-color: #1c1c1c;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  color: #fff;
}

.crypto-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.crypto-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.crypto-item:hover {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s;
  background-color: rgba(0, 0, 0, 0.1);
}

.crypto-icon {
  margin-right: 15px;
}

.crypto-icon img {
  width: 32px;
  height: 32px;
}

.crypto-details {
  display: flex;
  flex-direction: column;
}

.crypto-details h4 {
  margin: 0;
  font-size: 1.1em;
}

.crypto-details p {
  margin: 2px 0;
  font-size: 0.9em;
}

.positive {
  color: #4caf50;
}

.negative {
  color: #ff5252;
}

.crypto-table-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
}

.crypto-table {
  display: flex;
  flex-direction: column;
  width: max-content;
  /* Ensure the table takes the necessary width for scrolling */
}

.crypto-row {
  display: flex;
  margin-bottom: 30px;
}

.crypto-item {
  display: flex;
  border: 1px solid transparent;
  padding: 8px;
  width: 350px;
  /* Set a fixed width for each item */
  margin-right: 10px;
  position: relative;
  cursor: pointer;
  transition: box-shadow 0.3s, transform 0.3s;
  white-space: normal;
  /* Ensure text wraps within the item */
  word-wrap: break-word;
  /* Break words that are too long */
}

.crypto-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-5px);
}

/* Tooltip styles */
.tooltip {
  visibility: hidden;
  background-color: rgba(125, 123, 123, 0.7);
  color: black;
  text-align: center;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  padding: 5px;
  position: absolute;
  top: 70px;
  width: 95%;
  height: 25px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
}

.tooltip-visible {
  visibility: visible;
  opacity: 1;
}

.crypto-bar {
  width: 5px;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
}

.positive-bar {
  background-color: green;
}

.negative-bar {
  background-color: red;
}

.crypto-info {
  display: flex;
  justify-content: space-between;
  /* Distribute space between children */
  align-items: center;
  width: 100%;
  /* Make sure it takes the full width of the container */
  padding-left: 10px;
}

.crypto-symbol {
  flex: 1;
  /* Take all the available space on the left */
  display: flex;
  flex-direction: column;
  word-wrap: break-word;
  /* Ensure long words break to the next line */
}

.crypto-symbol h3 {
  margin: 0;
  font-size: 1em;
  font-weight: bold;
}

.crypto-symbol p {
  margin: 0;
  font-size: 0.9em;
  color: #666;
  word-wrap: break-word;
  /* Ensure long words break to the next line */
  white-space: normal;
  /* Ensure text wraps */
  overflow-wrap: break-word;
  /* Ensure text wraps */
}

.crypto-price {
  flex-shrink: 0;
  /* Prevent shrinking */
  text-align: center;
  /* Center align the text */
  font-size: 0.9em;
}

.crypto-price .price {
  font-weight: bold;
}

.crypto-change {
  flex: 1;
  /* Take all the available space on the right */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  /* Align to the right */
  font-size: 0.9em;
}

.crypto-change p {
  margin: 0;
}

.positive {
  color: green;
}

.negative {
  color: red;
}

.row-divider {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  width: 100%;
  /* Ensure the divider spans the entire width of the scrolling area */
  margin: 10px 0;
}

.market-summary {
  margin-top: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 5px;
  color: #000;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.market-summary h3 {
  margin: 0 0 0.75rem 0;
  color: #000;
}

.market-summary p {
  margin: 0;
  color: #000;
}

/* Responsive: full-width stacked cards on phones / small tablets */
@media (max-width: 900px) {
  .crypto-table-wrapper {
    overflow-x: visible;
    white-space: normal;
  }

  .crypto-table {
    width: 100%;
    max-width: 100%;
  }

  .crypto-row {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  .crypto-item {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
    box-sizing: border-box;
  }

  .crypto-watch {
    padding: 12px;
  }

  .search-bar {
    flex-direction: column;
    width: 100%;
    padding: 0 4px;
    box-sizing: border-box;
  }

  .search-bar input {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
    margin-bottom: 8px;
    box-sizing: border-box;
  }
}

@media (min-width: 901px) {
  .crypto-item {
    width: min(350px, 32vw);
    flex-shrink: 0;
  }
}

.search-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.search-bar input {
  padding: 10px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 3px;
  width: 60%;
  margin-right: 10px;
}

.search-bar button {
  padding: 10px 20px;
  font-size: 1em;
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.search-bar button:hover {
  background-color: #333333;
}

/* Add the bot chat styles from the previous example here */
.bot-chat-container {
  position: fixed;
  bottom: 100px;
  right: 30px;
  display: flex;
  align-items: flex-end;
  z-index: 1000;
  transition: all 0.3s ease;
  max-width: 350px;
}

.bot-image {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.3s ease;
  background: var(--bg-secondary);
  padding: 5px;
  box-shadow: var(--shadow-md);
}

.bot-image:hover {
  transform: scale(1.1);
}

.bot-message {
  background: var(--bg-secondary);
  padding: 1.5rem;
  border-radius: 15px;
  margin-right: 1rem;
  max-width: 300px;
  box-shadow: var(--shadow-md);
  position: relative;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.bot-message::after {
  content: '';
  position: absolute;
  right: -10px;
  bottom: 20px;
  width: 0;
  height: 0;
  border-left: 10px solid var(--bg-secondary);
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
}

.typing-animation {
  display: flex;
  gap: 0.3rem;
  align-items: center;
  padding: 0.5rem;
}

.dot {
  width: 8px;
  height: 8px;
  background: var(--text-secondary);
  border-radius: 50%;
  animation: bounce 1s infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.typed-message {
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-primary);
}

/* Visibility classes */
.bot-visible {
  opacity: 1;
  transform: translateY(0);
}

.bot-hidden {
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;
}

.message-visible {
  opacity: 1;
  transform: translateX(0);
}

.message-hidden {
  opacity: 0;
  transform: translateX(20px);
  pointer-events: none;
}

/* Dark mode adjustments */
:root[data-theme="dark"] .bot-message {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

:root[data-theme="dark"] .bot-message::after {
  border-left-color: var(--bg-secondary);
}

:root[data-theme="dark"] .dot {
  background: var(--text-secondary);
}
</style>
